import { useLanguage } from "../contexts/LanguageContext"
import { useRef, useState } from "react"
import { useIntersectionObserver } from "../hooks/useScrollAnimation"

export default function About() {
  const { t, isRTL, currentLanguage } = useLanguage()
  const sectionRef = useRef(null)
  const isVisible = useIntersectionObserver(sectionRef)
  const [activeStep, setActiveStep] = useState(1)

  // Function to get the appropriate track image based on language
  const getLanguageTrackImage = () => {
    const languageImageMap = {
      en: "/en_track.jpg",
      hi: "/hi_track.jpg",
      ar: "/ar_track.jpg",
      ur: "/urdu_track.jpg",
      fr: "/french_track.jpg",
      ko: "/korean_track.jpg",
      filipino: "/filipino_track.jpg",
      persian: "/persion_track.jpg",
      pashto: "/pasto_track.jpg"
    }

    return languageImageMap[currentLanguage] || languageImageMap.en
  }

  // Function to get the appropriate shipment image based on language
  const getLanguageShipmentImage = () => {
    const languageImageMap = {
      en: "/en_shipment.jpg",
      hi: "/hi_shipment.jpg",
      ar: "/ar_shipment.jpg",
      ur: "/ur_shipment.jpg",
      fr: "/french_shipment.jpg",
      ko: "/korean_shipment.jpg",
      filipino: "/filipino_shipment.jpg",
      persian: "/persion_shipment.jpg",
      pashto: "/pashto_shipment.jpg"
    }

    return languageImageMap[currentLanguage] || languageImageMap.en
  }

  // Function to get the appropriate transporter image based on language
  const getLanguageTransporterImage = () => {
    const languageImageMap = {
      en: "/en_transporter.jpg",
      hi: "/hi_transporter.jpg",
      ar: "/ar_transporter.jpg",
      ur: "/urdu_transporter.jpg",
      fr: "/french_transporter.jpg",
      ko: "/korean_transporter.jpg",
      filipino: "/filipino_transpoter.jpg",
      persian: "/persian_transporter.jpg",
      pashto: "/pashto_transporter.jpg"
    }

    return languageImageMap[currentLanguage] || languageImageMap.en
  }

  // Step images mapping
  const stepImages = {
    1: getLanguageShipmentImage(),
    2: getLanguageTransporterImage(),
    3: getLanguageTrackImage()
  }

  return (
    <>
      <section id="about" className="py-20 bg-gray-50 relative overflow-hidden" ref={sectionRef}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-10 w-32 h-32 bg-[#FF8500]/5 rounded-full animate-pulse"></div>
          <div
            className="absolute bottom-20 left-10 w-24 h-24 bg-[#FF8500]/10 rounded-full animate-bounce"
            style={{ animationDuration: "4s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            className={`text-center mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t("about.title")}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("about.description")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div
              className={`transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t("about.subtitle")}</h3>

              <div className="space-y-6">
                <div
                  className={`flex items-start ${isRTL() ? 'space-x-reverse space-x-4' : 'space-x-4'} transition-all duration-700 delay-500 cursor-pointer group ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-5"} ${activeStep === 1 ? "bg-[#FF8500]/5 rounded-lg p-4 -m-4" : "hover:bg-gray-100 rounded-lg p-4 -m-4"}`}
                  onClick={() => setActiveStep(1)}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeStep === 1 ? "bg-[#FF8500] scale-110" : "bg-gray-400 group-hover:bg-[#FF8500] group-hover:scale-110"}`}>
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 transition-colors duration-300 ${activeStep === 1 ? "text-[#FF8500]" : "text-gray-900 group-hover:text-[#FF8500]"}`}>
                      {t("about.step1.title")}
                    </h4>
                    <p className="text-gray-600">{t("about.step1.description")}</p>
                  </div>
                </div>

                <div
                  className={`flex items-start ${isRTL() ? 'space-x-reverse space-x-4' : 'space-x-4'} transition-all duration-700 delay-700 cursor-pointer group ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-5"} ${activeStep === 2 ? "bg-[#FF8500]/5 rounded-lg p-4 -m-4" : "hover:bg-gray-100 rounded-lg p-4 -m-4"}`}
                  onClick={() => setActiveStep(2)}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeStep === 2 ? "bg-[#FF8500] scale-110" : "bg-gray-400 group-hover:bg-[#FF8500] group-hover:scale-110"}`}>
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 transition-colors duration-300 ${activeStep === 2 ? "text-[#FF8500]" : "text-gray-900 group-hover:text-[#FF8500]"}`}>
                      {t("about.step2.title")}
                    </h4>
                    <p className="text-gray-600">{t("about.step2.description")}</p>
                  </div>
                </div>

                <div
                  className={`flex items-start ${isRTL() ? 'space-x-reverse space-x-4' : 'space-x-4'} transition-all duration-700 delay-900 cursor-pointer group ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-5"} ${activeStep === 3 ? "bg-[#FF8500]/5 rounded-lg p-4 -m-4" : "hover:bg-gray-100 rounded-lg p-4 -m-4"}`}
                  onClick={() => setActiveStep(3)}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${activeStep === 3 ? "bg-[#FF8500] scale-110" : "bg-gray-400 group-hover:bg-[#FF8500] group-hover:scale-110"}`}>
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 transition-colors duration-300 ${activeStep === 3 ? "text-[#FF8500]" : "text-gray-900 group-hover:text-[#FF8500]"}`}>
                      {t("about.step3.title")}
                    </h4>
                    <p className="text-gray-600">{t("about.step3.description")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`flex justify-center sm:justify-end lg:justify-center transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
            >
              <div className="relative">
                {/* Gradient border wrapper */}
                <div
                  className="p-1 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500"
                  style={{
                    background: 'linear-gradient(135deg, #FF8500 0%, #FF6B00 50%, #FF4500 100%)'
                  }}
                >
                  <img
                    key={activeStep}
                    src={stepImages[activeStep]}
                    alt={`Makkok App Interface - Step ${activeStep}`}
                    className="h-[480px] sm:h-[520px] w-auto rounded-3xl hover:scale-105 transition-all duration-500 animate-float bg-white"
                  />
                </div>
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#FF8500]/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
